// Waymark's docs example for startTopicSearch passes a fully-qualified URL
// ('https://waymark.com'). The wizard's businessWebsite field has no URL
// validation, so users routinely type bare domains like 'livestockexchange.com'.
// Passing a bare domain to Waymark's topic search appears to fail silently —
// the SDK returns a topicId but the topic never gets persisted, so the polling
// lambda 404s on every status check. Normalize to a valid `https://...` URL
// before handing off to the SDK.
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  // Strip leading `@` or other paste artifacts that aren't valid URL prefixes.
  const cleaned = trimmed.replace(/^[@\s]+/, '');
  // If it already has a protocol, keep it as-is.
  if (/^https?:\/\//i.test(cleaned)) return cleaned;
  // Otherwise prepend https. Removing any accidental leading `//`.
  return `https://${cleaned.replace(/^\/+/, '')}`;
}

// CTV-564: syntactic-only "does this look like a website?" guard. Used to
// reject obvious garbage (bare strings, missing TLD, spaces in the host)
// before kicking off a Waymark generation, since Waymark's own failure
// path can take a minute or more to return when the URL is unreachable.
// Does NOT verify the domain exists — only that the input has the shape of
// a URL with a real-looking host.
export function looksLikeWebsiteUrl(raw: string): boolean {
  const normalized = normalizeUrl(raw);
  if (!normalized) return false;
  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    return false;
  }
  const host = parsed.hostname;
  if (!host) return false;
  // Reject hosts with whitespace or other characters that wouldn't survive
  // a real DNS lookup — the URL constructor accepts more than we want.
  if (/[\s_]/.test(host)) return false;
  // Must have a dot with a 2+ char TLD after it.
  if (!/\.[a-z]{2,}$/i.test(host)) return false;
  return true;
}
